import { Component, OnInit } from '@angular/core';
import { AnalyticEvents } from '@app/enums';
import { ApplicantIndividualMessageService, ApplicantService } from '@app/modules/applicant-tracker/services';
import { RecruitingModuleMenuService } from '@app/modules/applicant-tracker/services/menus/recruiting-module-menu.service';
import { AnalyticService, NotifyService } from '@app/services';
import { ApplicantFilters } from '@applicant-tracker/enums/applicant-filters.enum';
import { ApplicantsIndexTable } from '@applicant-tracker/services/tables/applicants-table/applicants-index.table';
import { RoutingStateService } from '@common/services/routing-state.service';
import { Applicant } from '@models/recruiting/applicant.model';
import { JobFunnelMessageTemplate } from '@models/recruiting/job-funnel-message-template.model';
import { JobFunnel } from '@models/recruiting/job-funnel.model';
import { Column } from '@table/classes/column.class';
import { FilterValueChange } from '@table/interfaces/filter-value-change.interface';

@Component({
    templateUrl: './applicant-index.template.html',
    styleUrls: ['./applicant-index.style.scss'],
})
export class ApplicantIndexView implements OnInit {
    applicant: Applicant;
    applicantJobFunnelMessageTemplate: JobFunnelMessageTemplate;

    isChangeFunnelStageModalOpen = false;
    searchPlaceHolder = 'Search';
    searchWidth = 250;

    constructor(
        public table: ApplicantsIndexTable,
        public applicantIndividualMessageService: ApplicantIndividualMessageService,
        private applicantService: ApplicantService,
        private notify: NotifyService,
        private routingState: RoutingStateService,
        private analyticService: AnalyticService,
        public recruitingModuleMenuService: RecruitingModuleMenuService
    ) {
        this.applicantService.updateContext('index');
        this.applicantService.indexPaginator = this.table.paginator;
    }

    async ngOnInit(): Promise<void> {
        // TODO: Remove this once the routing state service working correctly.
        this.routingState.history;
        this.table.reload();
    }

    onAllFiltersCleared(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantsClearFilters);
    }

    onFilterChange(change: FilterValueChange): void {
        switch (change.field) {
            case ApplicantFilters.jobPosting:
                this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantsFilterAppliedJobPosting);
                break;
            case ApplicantFilters.status:
                this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantsFilterAppliedStatus);
                break;
            case ApplicantFilters.jobFunnel:
                this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantsFilterAppliedJobFunnel);
                break;
            case ApplicantFilters.rating:
                this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantsFilterAppliedRating);
                break;
            default:
                throw new Error('An analytic event is missing for applicant filter: ' + change.field);
        }
    }

    onSearchQueryChange(_query: string): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantsSearch);
    }

    async onChangeFunnelStage(dropdownSelection: { model: Applicant; column: Column }): Promise<void> {
        this.applicant = dropdownSelection.model;

        const jobFunnel: JobFunnel = await JobFunnel.with(['jobFunnelMessageTemplate']).find(
            this.applicant.jobFunnelId
        );

        if (!jobFunnel.jobFunnelMessageTemplate) {
            this.notify.success('applicant-tracker.applicant-status-changed');
            return;
        }

        this.isChangeFunnelStageModalOpen = true;

        this.applicantJobFunnelMessageTemplate = jobFunnel.jobFunnelMessageTemplate;
    }

    onCloseChangeFunnelStage(): void {
        this.isChangeFunnelStageModalOpen = false;

        this.notify.success('applicant-tracker.applicant-status-changed');

        this.analyticService.trackEvent(
            AnalyticEvents.RecruitingJobPostingFunnelChangeApplicantFunnelStageClickSkipEmail
        );
    }

    async onConfirmChangeFunnelStage(): Promise<void> {
        await this.applicantIndividualMessageService.sendApplicantIndividualMessage(
            this.applicant.id,
            this.applicantJobFunnelMessageTemplate
        );

        this.isChangeFunnelStageModalOpen = false;

        this.notify.success({
            key: 'applicant-tracker.email-sent-to-applicant',
            params: { subject: this.applicantJobFunnelMessageTemplate.subject },
        });

        this.notify.success('applicant-tracker.applicant-status-changed');

        this.analyticService.trackEvent(
            AnalyticEvents.RecruitingJobPostingFunnelChangeApplicantFunnelStageClickSendEmail
        );
    }

    async onExport(format: 'csv' | 'xlsx'): Promise<void> {
        const filters = {};

        for (const [key, value] of this.table.activeFilters.entries()) {
            filters[key] = value;
        }

        await this.applicantService.exportApplicants(
            format,
            this.table.paginator.search,
            filters,
            this.table.paginator.sort
        );

        switch (format) {
            case 'csv':
                this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantsExportCSV);
                break;
            case 'xlsx':
                this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantsExportXLSX);
                break;
        }
    }
}
